
import { Component, Vue } from "vue-property-decorator";
import { Organization } from "@/models/Organization";
import { RideitNotice } from '@/models/Rideit/RideitNotice';
import RideitCommunicationsForm from "@/components/Organization/Rideit/RideitCommunicationsForm.vue";
import RideitNoticeFormDialog from "@/components/Organization/Rideit/Notices/RideitNoticeFormDialog.vue";
import RideitNoticesTable from '@/components/Organization/Rideit/Notices/RideitNoticesTable.vue'
import restService from '@/services/restService';
import vuetifyToast from 'vuetify-toast';
import { getErrorMessage } from '@/util/errorMessageHelper';

export interface IRideitCommunicationsLoadingStates {
  fetchingNotices: boolean;
  savingNotice: boolean;
}

@Component({
  components: {
    RideitCommunicationsForm,
    RideitNoticeFormDialog,
    RideitNoticesTable,
  },
})
export default class RideitCommunications extends Vue {

  organization: Organization = this.getOrganization();

  showNoticeFormDialog: boolean = false;
  editedNotice: RideitNotice | null = null;

  notices: RideitNotice[] = [];
  noticesFilter: string = '';
  fetchNoticesError: string = '';

  loading: IRideitCommunicationsLoadingStates = {
    fetchingNotices: false,
    savingNotice: false,
  }

  getOrganization() {
    const organizationId = this.$route.params.organizationId;
    return this.$store.state.organization.organizations.find((o: Organization) => o.id === organizationId);
  }
  
  updateNoticesFilter(value: string) {
    this.noticesFilter = value;
  }

  createNewNotice() {
    this.editedNotice = new RideitNotice();
    this.showNoticeFormDialog = true;
  }

  editNotice(notice: RideitNotice) {
    this.editedNotice = new RideitNotice(notice);
    this.showNoticeFormDialog = true;
  }

  closeNoticeFormDialog() {
    this.showNoticeFormDialog = false;
    this.editedNotice = null;
  }

  async saveNotice(notice: RideitNotice) {
    const isEditing = Boolean(notice.id);
    let savedNotice = null;
    
    if (isEditing) {
      savedNotice = await this.updateNotice(notice);
    }
    else {
      savedNotice = await this.createNotice(notice);
    }
    
    const index = this.notices.findIndex(n => n.id === savedNotice.id);
    if (index > -1) {
      this.notices.splice(index, 1, savedNotice);
    } else {
      this.notices.push(savedNotice);
    }

    this.closeNoticeFormDialog();
  }

  async createNotice(notice: RideitNotice) {
    this.loading.savingNotice = true;
    try {
      const createdNotice = await restService.rideit.createNotice(notice.toCreateRequest(), this.organization.id);
      vuetifyToast.success(this.$t('rideIt.notices.create.success'));
      return new RideitNotice(createdNotice);
    } catch (error) {
      vuetifyToast.error(this.$t('rideIt.notices.create.error', { errorMessage: getErrorMessage(error) }));
    } finally {
      this.loading.savingNotice = false;
    }
  }

  async updateNotice(notice: RideitNotice) {
    this.loading.savingNotice = true;
    try {
      const updatedNotice = await restService.rideit.updateNotice(this.organization.id, notice);
      vuetifyToast.success(this.$t('rideIt.notices.update.success'));
      return new RideitNotice(updatedNotice);
    } catch (error) {
      vuetifyToast.error(this.$t('rideIt.notices.update.error', { errorMessage: getErrorMessage(error) }));
    } finally {
      this.loading.savingNotice = false;
    }
    return notice;
  }

  async deleteNotice(notice: RideitNotice) {
    this.loading.savingNotice = true;
    try {
      const deletedNotice = await restService.rideit.deleteNotice(notice.id, this.organization.id);
      const index = this.notices.findIndex(n => n.id === deletedNotice.id);
      if (index > -1)  {
        this.notices.splice(index, 1);
      }
      vuetifyToast.success(this.$t('rideIt.notices.delete.success'));
    } catch (error) {
      vuetifyToast.error(this.$t('rideIt.notices.delete.error', { errorMessage: getErrorMessage(error) }))
    } finally {
      this.loading.savingNotice = false;
    }
    this.closeNoticeFormDialog();
  }

  async mounted() {
    this.notices = await this.getNotices();
  }

  async getNotices() {
    this.fetchNoticesError = '';
    this.loading.fetchingNotices = true;
    try {
      const notices = await restService.rideit.getNotices(this.organization.id);
      return notices.map(n => new RideitNotice(n));
    } 
    catch (error) {
      this.fetchNoticesError = getErrorMessage(error);
    } 
    finally {
      this.loading.fetchingNotices = false;
    }
  }
}
